import { z } from 'zod';
import { AiConsumerTierSchema, AiTierSchema } from '../../shared/ai-tier';
import { APP_SLUG_PATTERN } from '../../shared/app-slug';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { SeatLimitSchema } from '../../entities/tenant/tenant.schema';
import { InstalledAppSchema } from '../../entities/organization/installed-app.schema';
/**
 * App Route Schema
 * Defines a navigation entry for an app
 */ /**
 * Text can be a plain string or a locale map, e.g. `{ "en": "Overview", "de": "Übersicht" }`.
 */ export const LocalizedTextSchema = z.union([
    z.string(),
    z.record(z.string(), z.string())
]);
/**
 * Copy that must exist in every UI locale. `LocalizedTextSchema` accepts any
 * partial map, so a missing or misspelt locale would degrade silently at render
 * — and `check:translations` cannot see manifest copy, it scopes to
 * `frontend/src`. Requiring the four keys makes an incomplete map fail the
 * manifest parse at bootstrap instead, which skips the app's registration and
 * demotes its stored registry row to `draft`.
 */ export const LocalizedCopySchema = z.object({
    de: z.string().min(1),
    en: z.string().min(1),
    fr: z.string().min(1),
    hu: z.string().min(1)
});
export const AppRouteSchema = z.object({
    path: z.string(),
    label: LocalizedTextSchema,
    icon: z.string().optional()
});
/**
 * The catalog tag an app carries.
 */ export const AppCategorySchema = z.enum([
    'analysis',
    'data-capture',
    'sales'
]);
/**
 * App Function Declaration Schema
 * Defines resource limits for a declared function
 */ export const AppFunctionDeclSchema = z.object({
    timeout: z.number().int().positive().default(30000),
    maxMemory: z.number().int().positive().default(128)
});
/**
 * App Storage Index Schema
 */ export const AppStorageIndexSchema = z.object({
    key: z.record(z.string(), z.union([
        z.literal(1),
        z.literal(-1)
    ])),
    unique: z.boolean().optional()
});
/**
 * App Storage Collection Declaration Schema
 *
 * `serverOnly` collections are reachable only from the app's own worker
 * functions (the app-runner storage path); the app-storage HTTP gateway treats
 * a client read of one as if the collection were undeclared. Opt-in and absent
 * by default, so no existing app's read surface changes.
 */ export const AppStorageCollectionDeclSchema = z.object({
    indexes: z.array(AppStorageIndexSchema).optional(),
    serverOnly: z.boolean().optional()
});
/**
 * App Storage Collection Name Schema
 * Leading underscores are reserved for platform-managed collections (e.g. `_runs`).
 */ export const AppStorageCollectionNameSchema = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/, 'Collection names must be alphanumeric (plus - and _) and must not start with an underscore');
/**
 * App Storage Declaration Schema
 */ export const AppStorageDeclSchema = z.object({
    collections: z.record(AppStorageCollectionNameSchema, AppStorageCollectionDeclSchema)
});
/**
 * App Files Declaration Schema
 * Opt-in to the generic App Files API (upload / presigned URL / delete) with an
 * explicit MIME allowlist and an optional per-app size cap (the platform
 * enforces a hard ceiling on top).
 */ export const AppFilesDeclSchema = z.object({
    mimeTypes: z.array(z.string()).min(1),
    maxSizeBytes: z.number().int().positive().optional(),
    /**
   * Days after upload at which the BINARY is deleted and every read path
   * refuses the file; the metadata row survives. Absent = kept indefinitely,
   * which is what every app but the converter wants.
   */ retentionDays: z.number().int().positive().optional()
});
/**
 * App Accounts Declaration Schema
 * Opt-in to `ctx.accounts` — making the Bliks account for a HubSpot company
 * (SAL-04). Accounts are platform data, so an app may make them only in the
 * one tenant its environment names: `tenantEnv` is the env variable that holds
 * that tenant's id. The platform reads it from the app's env, which operations
 * set per environment and no tenant can change; an unset variable means no
 * tenant may.
 */ export const AppAccountsDeclSchema = z.object({
    tenantEnv: z.string().regex(/^[A-Z][A-Z0-9_]*$/, 'tenantEnv names an env variable in UPPER_SNAKE_CASE')
});
/**
 * Model Slot Name Schema
 * A slot name is half of the `appId:slot` override key and lands in a Mongo
 * dot-path (`appModelOverrides.<appId>:<slot>`), so it must contain neither a
 * colon nor a dot.
 */ export const AppModelSlotNameSchema = z.string().regex(new RegExp(`^${APP_SLUG_PATTERN}$`), 'Model slot names must be lowercase alphanumeric with hyphens');
/**
 * One AI feature an app offers (plan 046) — a key the tenant can switch on or
 * off, the app checks with `useAiFeature(key)` and names in each `ctx.ai`
 * call. The key lands in a Mongo dot-path (`settings.ai.apps.<appId>.<key>`),
 * so it is slug-shaped. The label is what the AI settings show for the switch.
 */ export const AppAiFeatureSchema = z.object({
    key: z.string().regex(new RegExp(`^${APP_SLUG_PATTERN}$`), 'AI feature keys must be lowercase alphanumeric with hyphens'),
    label: LocalizedCopySchema
});
/**
 * Abstract AI needs an app declares. The platform binds tiers to concrete
 * models (superadmin can override per appId via `appModelOverrides`) — apps
 * never name models, and the worker never receives provider credentials.
 *
 * `slots` names sub-consumers of the text capability (e.g. `analyzer`,
 * `planner`) so one app can run its parts on different tiers and be overridden
 * independently under the `appId:slot` key. A `decision` slot serves
 * `ai.decide` calls only.
 */ export const AppAiDeclSchema = z.object({
    text: AiTierSchema.optional(),
    embeddings: z.boolean().optional(),
    slots: z.record(AppModelSlotNameSchema, AiConsumerTierSchema).optional(),
    /**
     * The features a tenant switches one by one. An app that declares them
     * runs its AI only behind the tenant's `enableAI` and the feature's switch;
     * an app without them keeps today's unswitched `ctx.ai`.
     */ features: z.array(AppAiFeatureSchema).optional()
}).refine((decl)=>decl.text !== undefined || decl.embeddings === true, {
    message: 'ai declaration must request text and/or embeddings'
}).refine((decl)=>decl.slots === undefined || decl.text !== undefined, {
    message: 'model slots require the text capability'
}).refine((decl)=>new Set(decl.features?.map((feature)=>feature.key)).size === (decl.features?.length ?? 0), {
    message: 'ai feature keys must be unique'
});
/**
 * Chat Kind Name Schema
 * Kind names are unique per app and address the bundle built from
 * `chat/<kind>.ts` into `dist/chat/<kind>.cjs`, so they must be path-safe.
 */ export const AppChatKindNameSchema = z.string().regex(new RegExp(`^${APP_SLUG_PATTERN}$`), 'Chat kind names must be lowercase alphanumeric with hyphens');
/**
 * App Chat Declaration Schema
 * Declares a chat kind the app defines via the app-sdk's `defineChat`. The
 * resource limits bound a single worker run (one tool call, one initializer
 * run, or one describe), not a whole conversation.
 *
 * `modelSlot` binds the kind to one of the manifest's `ai.slots` instead of the
 * app-wide `ai.text` tier.
 */ export const AppChatDeclSchema = z.object({
    timeout: z.number().int().positive().default(30000),
    maxMemory: z.number().int().positive().default(128),
    modelSlot: AppModelSlotNameSchema.optional()
});
/**
 * The permission scopes the platform acts on — the single vocabulary behind
 * the app-runtime grant table and the seat-panel disclosure (ADR 0018). Not a
 * parse gate: the manifest's `permissions` field stays free-form because
 * registry rows are unvalidated blobs; a string outside this list grants
 * nothing and is displayed raw.
 */ export const APP_PERMISSION_SCOPES = [
    'read:processes',
    'read:process-steps',
    'read:surveys',
    'read:systems',
    'read:roles',
    'read:teams',
    'read:departments',
    'read:org-members',
    'read:artifacts',
    'read:external-contacts',
    'read:process-chains'
];
/**
 * App Manifest Schema
 * Describes what an app can do — permissions, functions, storage, routes
 */ export const AppManifestSchema = z.object({
    id: z.string().regex(new RegExp(`^${APP_SLUG_PATTERN}$`), 'App ID must be lowercase alphanumeric with hyphens'),
    name: z.string().min(1),
    description: LocalizedCopySchema.optional(),
    category: AppCategorySchema,
    permissions: z.array(z.string()).default([]),
    routes: z.array(AppRouteSchema).default([]),
    functions: z.record(z.string(), AppFunctionDeclSchema).default({}),
    chat: z.record(AppChatKindNameSchema, AppChatDeclSchema).default({}),
    files: AppFilesDeclSchema.optional(),
    storage: AppStorageDeclSchema.optional(),
    ai: AppAiDeclSchema.optional(),
    /**
     * Whether the app may hand members tasks (`ctx.tasks.create`). A write into
     * tenant data, so it is declared like `files` rather than implied: an app
     * that leaves it out has no `tasks` capability.
     */ tasks: z.boolean().optional(),
    accounts: AppAccountsDeclSchema.optional(),
    /**
     * Whether the app may ask for Freigaben (`ctx.approvals`). Reviewers get
     * tasks through the platform, so it needs no `tasks` of its own.
     */ approvals: z.boolean().optional(),
    env: z.array(z.string()).default([])
}).superRefine((manifest, ctx)=>{
    // The variable that names the tenant must reach the app's env like every
    // other: an undeclared one is never loaded, so the capability would be
    // silently dead in every environment.
    if (manifest.accounts && !manifest.env.includes(manifest.accounts.tenantEnv)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'accounts',
                'tenantEnv'
            ],
            message: `accounts.tenantEnv '${manifest.accounts.tenantEnv}' must be listed in env`
        });
    }
    // A chat kind runs a model, and the model it runs on is the manifest's
    // `ai.text` tier (plus the superadmin per-app override). Without a
    // declaration there is no tier to resolve, and defaulting to one would
    // silently put an app on the expensive model because its manifest was
    // incomplete — the same reason `AiProxyHost` refuses `generateStructured`
    // for an app that declares no `ai.text`.
    if (Object.keys(manifest.chat).length > 0 && !manifest.ai?.text) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'ai',
                'text'
            ],
            message: 'An app that declares chat kinds must declare an ai.text tier for them to run on'
        });
    }
    // A Map rather than `in` or a bare index: the slot-name regex admits
    // `constructor`, which every object inherits.
    const declaredSlots = new Map(Object.entries(manifest.ai?.slots ?? {}));
    for (const [kind, decl] of Object.entries(manifest.chat)){
        if (!decl.modelSlot) continue;
        const slotTier = declaredSlots.get(decl.modelSlot);
        if (slotTier === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'chat',
                    kind,
                    'modelSlot'
                ],
                message: `Chat kind '${kind}' names model slot '${decl.modelSlot}', which the manifest's ai.slots does not declare`
            });
        } else if (slotTier === 'decision') {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'chat',
                    kind,
                    'modelSlot'
                ],
                message: `Chat kind '${kind}' names model slot '${decl.modelSlot}', which is a decision slot — a chat kind needs a high or low slot`
            });
        }
    }
});
/**
 * App Registry Entry Schema
 * Represents a registered app in the SuperAdmin DB
 */ export const AppRegistryEntrySchema = z.object({
    _id: z.string().optional(),
    appId: z.string().regex(new RegExp(`^${APP_SLUG_PATTERN}$`)),
    manifest: AppManifestSchema,
    functionBundles: z.record(z.string(), z.string()).default({}),
    /** Resolved `dist/chat/<kind>.cjs` paths, keyed by chat kind. */ chatBundles: z.record(z.string(), z.string()).default({}),
    status: z.enum([
        'draft',
        'published'
    ]).default('published')
});
/**
 * The named-seat state of an installed app, exposed by MANAGEMENT reads only.
 * `seatLimit` is the superadmin-set cap (absent/null = unlimited, 0 = none
 * assignable) and `assignedUserIds` the org-manager-set assignments; org
 * managers reach an app implicitly and consume no seat.
 *
 * A shape rather than a schema so the single definition can be spread both into
 * `ManagedInstalledAppSchema` (the stored subdoc) and into the management list
 * entry (which also carries the registry's name and routes).
 */ const appSeatFields = {
    seatLimit: SeatLimitSchema.nullable().optional(),
    assignedUserIds: z.array(z.string()).optional()
};
/**
 * The superadmin-set per-app configuration, exposed by MANAGEMENT reads only —
 * it is commercial state (what a tenant was sold), so it rides alongside the
 * seat fields rather than on the base schema.
 *
 * Opaque here on purpose: the platform stores it and forwards it to the app as
 * `ctx.appConfig` without knowing any app's shape, and the write endpoint
 * validates it against that app's schema from `getAppConfigSchema` instead.
 */ const appConfigField = {
    config: z.unknown().optional()
};
/**
 * Managed Installed App Schema
 * The installed-app subdoc as the MANAGEMENT surfaces see it. `InstalledApp`
 * itself stays narrow on purpose: it is embedded in the organization document,
 * and several of the org-serializing paths return the raw doc rather than a
 * parse of this schema — so the narrow shape documents the contract but is not
 * what enforces it. The actual control is the CASL `Read`/`Update Organization`
 * grant (partner admin, system admin, org manager), which keeps plain members
 * off those paths in the first place.
 */ export const ManagedInstalledAppSchema = InstalledAppSchema.extend({
    ...appSeatFields,
    ...appConfigField
});
/**
 * Activate App Response Schema
 */ export const ActivateAppResponseSchema = z.object({
    app: InstalledAppSchema
});
/**
 * List Installed Apps Response Schema
 * GET /api/v2/apps
 *
 * One schema behind three views: the seat fields and the per-app config are
 * populated only by the management views (`?all=true` and the superadmin tenant
 * catalog), never by the user view — hence optional rather than required.
 *
 * `permissions` is management-view only, like the seat fields: the app's
 * declared scope list from the registry manifest, which the seat panel turns
 * into the disclosure of what a seat grants. Optional rather than defaulted —
 * a Zod default would make it required in the inferred type without ever
 * running, since the frontend reads the response through a bare axios generic.
 */ export const ListInstalledAppsResponseSchema = z.object({
    apps: z.array(z.object({
        appId: z.string(),
        name: z.string(),
        // Optional because a stored manifest is an unvalidated blob — a legacy
        // row may have been written before the manifest carried a `category`.
        category: AppCategorySchema.optional(),
        routes: z.array(AppRouteSchema),
        permissions: z.array(z.string()).optional(),
        enabled: z.boolean(),
        // Management-view only, like the seat fields — the user view never sees a
        // disallowed app at all, because `isEntitledToApp` filters it out.
        allowed: z.boolean().optional(),
        /** The manifest's `ai.features`, for the tenant's AI settings (plan 046). */ aiFeatures: z.array(AppAiFeatureSchema).optional(),
        ...appSeatFields,
        ...appConfigField
    }))
});
/**
 * Set App Seat Limit Request Schema
 * POST /api/v2/tenants/:tenantId/apps/:appId/seat-limit — `null` clears the cap
 * back to unlimited.
 */ export const SetAppSeatLimitRequestSchema = z.object({
    seatLimit: SeatLimitSchema.nullable()
});
export const SetAppSeatLimitResponseSchema = z.object({
    app: ManagedInstalledAppSchema
});
/**
 * Set App Config Request Schema
 * PUT /api/v2/tenants/:tenantId/apps/:appId/config — a full replace of the
 * app's per-tenant config. Opaque on the wire: the service validates the body
 * against `getAppConfigSchema(appId)`, so the shape is the app's, not this
 * endpoint's.
 */ export const SetAppConfigRequestSchema = z.object({
    config: z.unknown()
});
export const SetAppConfigResponseSchema = z.object({
    app: ManagedInstalledAppSchema
});
/**
 * Set App Allowed Request Schema
 * PUT /api/v2/tenants/:tenantId/apps/:appId/allowed — the superadmin's switch.
 * Withdrawing it keeps the subdoc, so seats and config survive; only `lock`
 * discards them.
 */ export const SetAppAllowedRequestSchema = z.object({
    allowed: z.boolean()
});
export const SetAppAllowedResponseSchema = z.object({
    app: ManagedInstalledAppSchema
});
/**
 * Update App Seats Request Schema
 * PUT /api/v2/apps/:appId/seats — a full replace, so the assignment set is
 * written atomically rather than accumulated add/remove by add/remove.
 */ export const UpdateAppSeatsRequestSchema = z.object({
    userIds: z.array(ObjectIdSchema)
});
export const UpdateAppSeatsResponseSchema = z.object({
    app: ManagedInstalledAppSchema
});
/**
 * List App Registry Response Schema
 * GET /api/v2/app-registry — returns all published apps from the global registry
 */ export const ListAppRegistryResponseSchema = z.object({
    apps: z.array(z.object({
        appId: z.string(),
        name: z.string(),
        description: LocalizedCopySchema.optional(),
        // Optional because the row may have been written by a different backend
        // than the one serving this response — an older deploy, or a peer dev
        // stack re-upserting the shared local registry from its own older
        // manifests — and that manifest carried no `category`.
        category: AppCategorySchema.optional(),
        /**
       * The manifest icon of the app's first route, so a candidate that is not
       * installed anywhere yet still renders its own icon instead of the
       * fallback. Optional like `category`: a route may declare none.
       */ icon: z.string().optional(),
        status: z.string(),
        /** Manifest ai declaration — drives the superadmin per-app model override UI. */ ai: AppAiDeclSchema.optional()
    }))
});
/**
 * Lock App Response Schema
 * POST /api/v2/tenants/:tenantId/apps/:appId/lock
 */ export const LockAppResponseSchema = z.object({
    appId: z.string()
});

//# sourceMappingURL=app-registry.schema.js.map